import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Globe, Smartphone, Cloud, Server, ShoppingCart } from 'lucide-react';
import { translations } from '../utils/translations';

export const Projects = ({ language = 'en' }) => {
  const t = translations[language]?.projects || translations.en.projects;

  const projects = [
    {
      category: t.categories.corporate.title,
      items: t.categories.corporate.items,
      description: t.categories.corporate.description,
      icon: Globe,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    },
    {
      category: t.categories.ecommerce.title,
      items: t.categories.ecommerce.items,
      description: t.categories.ecommerce.description,
      icon: ShoppingCart,
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
    },
    {
      category: t.categories.mobile.title,
      items: t.categories.mobile.items,
      description: t.categories.mobile.description,
      icon: Smartphone,
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    },
    {
      category: t.categories.cloud.title,
      items: t.categories.cloud.items,
      description: t.categories.cloud.description,
      icon: Cloud,
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    },
    {
      category: t.categories.network.title,
      items: t.categories.network.items,
      description: t.categories.network.description,
      icon: Server,
      color: 'text-cyan-600 dark:text-cyan-400',
      bgColor: 'bg-cyan-50 dark:bg-cyan-900/20',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="projects" className="py-20 md:py-28 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm uppercase tracking-wide">
            {t.header.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 dark:text-white mt-3 mb-4">
            {t.header.title}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {t.header.description}
          </p>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {projects.map((project, index) => {
            const Icon = project.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group bg-white dark:bg-gray-900 rounded-xl p-6 shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-300 hover:-translate-y-2"
              >
                {/* Icon */}
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${project.bgColor} group-hover:scale-110 transition-transform duration-300`}>
                    <Icon size={24} className={project.color} />
                  </div>
                  <ExternalLink 
                    size={20} 
                    className="text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-all duration-300" 
                  />
                </div>

                {/* Category */}
                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                  {project.category}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                  {project.description}
                </p>

                {/* Project Items (commented out as in original) */}
                {/* <div className="space-y-2">
                  {project.items.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center text-sm text-gray-700 dark:text-gray-200 font-medium group-hover:text-gray-800 dark:group-hover:text-white transition-colors duration-300"
                    >
                      <div className="w-1.5 h-1.5 bg-blue-500 dark:bg-blue-400 rounded-full mr-2 flex-shrink-0"></div>
                      {item}
                    </div>
                  ))}
                </div> */}
              </motion.div>
            );
          })}
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mt-16 bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                {t.stats.projects}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                {t.stats.projectsLabel}
              </div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                {t.stats.clients}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                {t.stats.clientsLabel}
              </div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                {t.stats.industries}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                {t.stats.industriesLabel}
              </div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 dark:text-orange-400 mb-2">
                {t.stats.experience}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                {t.stats.experienceLabel}
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="text-center mt-12"
        >
          <p className="text-gray-600 dark:text-gray-300 mb-4 text-lg">
            {t.cta.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToContact}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition-all duration-300 hover:shadow-lg font-semibold"
            >
              {t.cta.buttons.primary}
            </button>
            <button
              onClick={scrollToServices}
              className="px-8 py-3 border-2 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white rounded-lg transition-all duration-300 font-semibold"
            >
              {t.cta.buttons.secondary}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};